import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaSave, FaTimes, FaPlus, FaFileAlt, FaEnvelope, FaSearch, FaTrash, FaUsers, FaCheck } from 'react-icons/fa';
import './WarehousesInstructions.css';
import Button from './UI/Button';

const WarehousesInstructions = () => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState([]);
  const [clients, setClients] = useState([]);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [newWarehouse, setNewWarehouse] = useState({
    name: '',
    email: '',
    instructions: '',
    client_ids: []
  });

  const ITEMS_PER_PAGE = 10;
  
  // Filter warehouses based on search query
  const filteredWarehouses = warehouses.filter(warehouse => 
    warehouse.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    warehouse.email.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredWarehouses.length / ITEMS_PER_PAGE);
  const paginatedWarehouses = filteredWarehouses.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    fetchWarehouses();
    fetchClients();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/warehouses-edn', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/clients-edn', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleUpdateWarehouse = async (warehouseId) => {
    if (!editingWarehouse) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/logistics/warehouses-edn/${warehouseId}`,
        {
          name: editingWarehouse.name,
          email: editingWarehouse.email,
          instructions: editingWarehouse.instructions,
          client_ids: editingWarehouse.client_ids
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchWarehouses();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to update warehouse:', error);
    }
  };

  const handleCreateWarehouse = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/logistics/warehouses-edn', newWarehouse, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setNewWarehouse({
        name: '',
        email: '',
        instructions: '',
        client_ids: []
      });
      setSelectedClientIds([]);
      fetchWarehouses();
    } catch (error) {
      console.error('Failed to create warehouse:', error);
    }
  };

  const handleDeleteWarehouse = async (warehouseId) => {
    if (!window.confirm(t('warehousesInstructions.confirmDelete'))) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/logistics/warehouses-edn/${warehouseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchWarehouses();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete warehouse:', error);
    }
  };

  const handleWarehouseClick = (warehouse) => {
    setSelectedWarehouse(warehouse);
    // Extract client IDs from the clients array for editing
    const clientIds = warehouse.clients ? warehouse.clients.map(client => client.id) : [];
    const warehouseWithClientIds = {
      ...warehouse,
      client_ids: clientIds // Add client_ids for compatibility with edit logic
    };
    setEditingWarehouse(warehouseWithClientIds);
    setSelectedClientIds(clientIds);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowCreateModal(false);
    setEditingWarehouse(null);
    setSelectedWarehouse(null);
    setSelectedClientIds([]);
  };

  const handleClientToggle = (clientId, isCreate = false) => {
    if (isCreate) {
      const updatedClientIds = selectedClientIds.includes(clientId)
        ? selectedClientIds.filter(id => id !== clientId)
        : [...selectedClientIds, clientId];
      
      setSelectedClientIds(updatedClientIds);
      setNewWarehouse({
        ...newWarehouse,
        client_ids: updatedClientIds
      });
    } else {
      const currentClientIds = editingWarehouse.client_ids || [];
      const updatedClientIds = currentClientIds.includes(clientId)
        ? currentClientIds.filter(id => id !== clientId)
        : [...currentClientIds, clientId];

      setEditingWarehouse({
        ...editingWarehouse,
        client_ids: updatedClientIds
      });
      setSelectedClientIds(updatedClientIds);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const getAssociatedClientsCount = (warehouse) => {
    // Use client_count from API response, fallback to clients array length
    return warehouse.client_count || (warehouse.clients ? warehouse.clients.length : 0);
  };

  const ClientSelector = ({ selectedIds, onToggle, isCreate = false }) => {
    const [clientSearchQuery, setClientSearchQuery] = useState('');

    // Filter clients based on search query
    const filteredClients = clients.filter(client =>
      client.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
      client.client_id.toLowerCase().includes(clientSearchQuery.toLowerCase())
    );

    return (
      <div className="client-selector">
        <div className="client-selector-header">
          <h4>{t('warehousesInstructions.selectClients')}</h4>
          <span className="selected-count">
            {selectedIds.length} {t('warehousesInstructions.clientsSelected')}
          </span>
        </div>
        
        <div className="client-search-container">
          <div className="search-container">
            <input
              type="text"
              value={clientSearchQuery}
              onChange={(e) => setClientSearchQuery(e.target.value)}
              placeholder={t('warehousesInstructions.searchClients')}
              className="search-field-select"
            />
            {clientSearchQuery && (
              <button 
                className="clear-search" 
                onClick={() => setClientSearchQuery('')}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div className="client-list">
          {filteredClients.length > 0 ? (
            filteredClients.map(client => (
              <div 
                key={client.id} 
                className={`client-item ${selectedIds.includes(client.id) ? 'selected' : ''}`}
                onClick={() => onToggle(client.id, isCreate)}
              >
                <div className="client-info">
                  <span className="client-name">{client.name}</span>
                  <span className="client-id">{client.client_id}</span>
                </div>
                <div className="client-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(client.id)}
                    onChange={() => onToggle(client.id, isCreate)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="no-clients-found">
              <FaSearch className="no-results-icon" />
              <span>{t('warehousesInstructions.noClientsFound')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="warehouses-instructions-container">
      <div className="table-controls">
        <div className="filters-container">
          <div className="search-container">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('common.search')}
              className="search-field-select"
            />
            {searchQuery && (
              <button 
                className="clear-search" 
                onClick={() => setSearchQuery('')}
              >
                <FaTimes />
              </button>
            )}
          </div>
          
          <Button 
            variant="primary"
            className="create-agent-button" 
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus /> {t('warehousesInstructions.createNew')}
          </Button>
        </div>
        
        <div className="pagination">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            {t('common.previous')}
          </button>
          <span>
            {currentPage} / {Math.max(1, totalPages)}
          </span>
          <button 
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            {t('common.next')}
          </button>
        </div>
      </div>

      <div className="warehouses-table-container">
        {paginatedWarehouses.length > 0 ? (
          <table className="warehouses-table">
            <thead>
              <tr>
                <th>{t('warehousesInstructions.name')}</th>
                <th>{t('warehousesInstructions.email')}</th>
                <th>{t('warehousesInstructions.instructions')}</th>
                <th>{t('warehousesInstructions.clients')}</th>
                <th className="actions-column">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedWarehouses.map(warehouse => (
                <tr key={warehouse.id} className="warehouse-row">
                  <td className="warehouse-name">
                    <div className="name-cell">
                      {warehouse.name}
                    </div>
                  </td>
                  <td className="warehouse-email">
                    <div className="email-cell">
                      <div className="status-cell">
                        <FaEnvelope className={`status-icon ${warehouse.email && warehouse.email.trim() !== '' ? 'success' : 'failure'}`} />
                        <span className="status-icon-text">
                          {warehouse.email && warehouse.email.trim() !== '' ? 
                            <FaCheck className="check-icon" /> : <FaTimes className="cross-icon" />}
                        </span>
                      </div>
                      {warehouse.email && (
                        <div className="email-address">
                          {warehouse.email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="warehouse-instructions">
                    <div className="status-cell">
                      <FaFileAlt className={`status-icon ${warehouse.instructions ? 'success' : 'failure'}`} />
                      <span className="status-icon-text">
                        {warehouse.instructions ? <FaCheck className="check-icon" /> : <FaTimes className="cross-icon" />}
                      </span>
                    </div>
                  </td>
                  <td className="warehouse-clients">
                    <div className="clients-cell">
                      <div className="status-cell">
                        <FaUsers className={`status-icon ${getAssociatedClientsCount(warehouse) > 0 ? 'success' : 'failure'}`} />
                        <span className="clients-count">
                          {getAssociatedClientsCount(warehouse)} {t('warehousesInstructions.clients').toLowerCase()}
                        </span>
                      </div>
                      {warehouse.clients && warehouse.clients.length > 0 && (
                        <div className="clients-preview">
                          {warehouse.clients.slice(0, 2).map(client => (
                            <span key={client.id} className="client-tag">
                              {client.name}
                            </span>
                          ))}
                          {warehouse.clients.length > 2 && (
                            <span className="more-clients">
                              +{warehouse.clients.length - 2} {t('common.more')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="warehouse-actions">
                    <div className="action-buttons">
                      <Button
                        variant="tertiary"
                        className="edit-button"
                        onClick={() => handleWarehouseClick(warehouse)}
                      >
                        <FaEdit />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-results">
            <div className="no-results-content">
              <FaSearch className="no-results-icon" />
              <div className="no-results-text">
                {searchQuery ? t('common.noSearchResults') : t('warehousesInstructions.noWarehouses')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modalai">
          <div className="modal-content warehouse-modal">
            <h3>{t('warehousesInstructions.editWarehouse')}</h3>
            <div className="form-group">
              <label>{t('warehousesInstructions.name')}:</label>
              <input
                type="text"
                value={editingWarehouse.name}
                onChange={(e) => setEditingWarehouse({
                  ...editingWarehouse,
                  name: e.target.value
                })}
                className="ai-agent-input"
              />
            </div>
            <div className="form-group">
              <label>{t('warehousesInstructions.email')}:</label>
              <input
                type="email"
                value={editingWarehouse.email}
                onChange={(e) => setEditingWarehouse({
                  ...editingWarehouse,
                  email: e.target.value
                })}
                className="ai-agent-input"
              />
            </div>
            <div className="form-group">
              <label>{t('warehousesInstructions.instructions')}:</label>
              <textarea
                value={editingWarehouse.instructions}
                onChange={(e) => setEditingWarehouse({
                  ...editingWarehouse,
                  instructions: e.target.value
                })}
                rows="8"
                className="ai-agent-textarea"
              />
            </div>
            <div className="form-group">
              <label>{t('warehousesInstructions.associatedClients')}:</label>
              <ClientSelector 
                selectedIds={selectedClientIds}
                onToggle={handleClientToggle}
              />
            </div>
            <div className="modal-actions">
              <Button 
                variant="primary"
                onClick={() => handleUpdateWarehouse(selectedWarehouse.id)}
              >
                <FaSave /> {t('common.save')}
              </Button>
              <Button 
                variant="danger"
                onClick={() => handleDeleteWarehouse(selectedWarehouse.id)}
              >
                <FaTrash /> {t('common.delete')}
              </Button>
              <Button 
                variant="secondary"
                onClick={handleCloseModal}
              >
                <FaTimes /> {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modalai">
          <div className="modal-content warehouse-modal">
            <h3>{t('warehousesInstructions.createNew')}</h3>
            <div className="form-group">
              <label>{t('warehousesInstructions.name')}:</label>
              <input
                type="text"
                value={newWarehouse.name}
                onChange={(e) => setNewWarehouse({
                  ...newWarehouse,
                  name: e.target.value
                })}
                className="ai-agent-input"
              />
            </div>
            <div className="form-group">
              <label>{t('warehousesInstructions.email')}:</label>
              <input
                type="email"
                value={newWarehouse.email}
                onChange={(e) => setNewWarehouse({
                  ...newWarehouse,
                  email: e.target.value
                })}
                className="ai-agent-input"
              />
            </div>
            <div className="form-group">
              <label>{t('warehousesInstructions.instructions')}:</label>
              <textarea
                value={newWarehouse.instructions}
                onChange={(e) => setNewWarehouse({
                  ...newWarehouse,
                  instructions: e.target.value
                })}
                rows="8"
                className="ai-agent-textarea"
              />
            </div>
            <div className="form-group">
              <label>{t('warehousesInstructions.selectClients')}:</label>
              <ClientSelector 
                selectedIds={selectedClientIds}
                onToggle={handleClientToggle}
                isCreate={true}
              />
            </div>
            <div className="modal-actions">
              <Button 
                variant="primary"
                onClick={handleCreateWarehouse}
              >
                <FaSave /> {t('common.create')}
              </Button>
              <Button 
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
              >
                <FaTimes /> {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehousesInstructions;
